import {useEffect, useState} from '@wordpress/element';
import {useCompare} from '../../../store/compare';

export const CompareDialog = ({open, onClose}) => {
    const {
        items,
        remove,
        clear,
        reloadAll,
        loading,
        all,
        specNamesAll,
        specNamesShared,
        hasShared
    } = useCompare();

    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        if (open) {
            reloadAll();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!hasShared) {
            setShowAll(true);
        }
    }, [open, hasShared]);

    if (!open) return null;

    const hasSharedToggle = hasShared && specNamesShared?.length > 0 && items.length > 1;
    const specNamesToShow = showAll || !hasSharedToggle ? specNamesAll : specNamesShared;
    const close = () => onClose?.();

    return (
        <div className="modal modal-open modal-bottom md:modal-middle !z-[1002]">
            <div
                className="modal-box p-0 w-full !max-w-7xl h-[90vh] md:rounded-2xl flex flex-col mx-auto overflow-hidden">

                <div
                    className="bg-white px-4 sm:px-8 md:px-10 py-4 sm:py-6 md:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-base-200">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black pt-dark-blue leading-tight">
                            Compare Products
                        </h2>
                        {loading && <span className="loading loading-spinner loading-md text-[#00A1FF]"></span>}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        {items.length > 0 && (
                            <button
                                className="btn btn-ghost pt-btn-pill border-none text-[#044165]/70 hover:bg-base-100 px-4 sm:px-5 h-9 sm:h-10 min-h-0 text-[11px] sm:text-xs font-bold uppercase tracking-wide"
                                onClick={clear}
                            >
                                Clear All
                            </button>
                        )}
                        <button
                            className="btn pt-btn-pill bg-[#00A1FF] hover:bg-[#00A1FF]/90 border-none text-white px-6 sm:px-8 md:px-10 h-10 sm:h-11 md:h-12 min-h-0 text-sm font-bold"
                            onClick={close}
                        >
                            Close
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto bg-white px-4 sm:px-8 md:px-10 pb-6 sm:pb-8 md:pb-10">
                    {items.length === 0 ? (
                        <div className="py-12 text-center">
                            <div className="text-[#044165]/60 font-medium mb-4">Add products to compare specs.</div>
                            <button className="btn btn-primary pt-btn-pill" onClick={close}>Back to Shop</button>
                        </div>
                    ) : (
                        <div className="min-w-full">
                            <table
                                className="table w-full table-fixed border-separate border-spacing-0 text-xs sm:text-sm">
                                <colgroup>
                                    <col className="w-[220px]"/>
                                    {items.map((_, i) => (
                                        <col key={i} className="min-w-[220px]"/>
                                    ))}
                                </colgroup>

                                <thead>
                                <tr className="bg-white">
                                    <th className="sticky top-0 left-0 z-50 bg-white border-b border-base-100 h-64 p-0">
                                        <div className="flex flex-col justify-end h-full pb-8 px-6">
                                            <div
                                                className={`flex items-center justify-start gap-3 group ${
                                                    hasSharedToggle ? 'cursor-pointer' : 'cursor-default opacity-40'
                                                }`}
                                                onClick={() => {
                                                    if (!hasSharedToggle) return;
                                                    setShowAll(!showAll);
                                                }}
                                            >
                                                <div
                                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                                        showAll ? 'bg-[#00A1FF] border-[#00A1FF]' : 'border-base-300 bg-white'
                                                    }`}
                                                >
                                                    {showAll && (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-3.5 w-3.5 text-white"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span
                                                    className="text-[11px] font-black pt-dark-blue uppercase tracking-widest select-none">
                                                        {showAll ? 'Show Only Shared Specs' : 'Show All Specs'}
                                                    </span>
                                            </div>
                                        </div>
                                    </th>
                                    {items.map((p) => (
                                        <th
                                            key={p.sku}
                                            className="sticky top-0 z-40 bg-white border-b border-base-100 h-auto md:h-64 p-3 sm:p-4 align-top"
                                        >
                                            <div className="relative group h-full">
                                                <button
                                                    className="absolute top-0 right-0 w-8 h-8 bg-base-100 shadow-md rounded-full flex items-center justify-center text-[#044165]/40 hover:text-error opacity-0 group-hover:opacity-100 transition-opacity z-50"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        remove(p.sku);
                                                    }}
                                                >
                                                    <span className="text-xl">&times;</span>
                                                </button>

                                                <a
                                                    href={p.href || `/product/${p.woocommerceSlug}`}
                                                    className="flex flex-col lg:flex-row items-center lg:items-stretch gap-3 sm:gap-4 md:gap-6 h-full unstyled-link hover:opacity-80 transition-opacity text-left"
                                                    onClick={(e) => {

                                                        if (e.target.closest('button')) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                >
                                                    <div
                                                        className="w-28 h-32 sm:w-32 sm:h-36 md:w-[140px] md:h-40 shrink-0">
                                                        <img
                                                            src={p.image}
                                                            alt={p.name}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    </div>
                                                    <div
                                                        className="flex flex-col justify-center min-w-0 no-underline mt-2 md:mt-0">
                                                        <div
                                                            className="text-xs sm:text-sm font-bold text-[#044165]/60 mb-0.5 sm:mb-1 truncate no-underline">
                                                            {p.manufacturer || p.brand}
                                                        </div>
                                                        <div
                                                            className="text-lg xl:text-2xl font-black pt-dark-blue leading-tight no-underline">
                                                            ${Number(p.salePrice).toFixed(2)}
                                                        </div>
                                                        {p.regularPrice > p.salePrice && (
                                                            <div
                                                                className="text-[10px] sm:text-[11px] text-[#044165]/40 font-bold line-through no-underline">
                                                                was ${Number(p.regularPrice).toFixed(2)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </a>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                                </thead>

                                <tbody className="text-xs sm:text-sm">
                                {specNamesToShow.map((name) => {
                                    const vals = all.get(name) || [];
                                    return (
                                        <tr key={name} className="hover:bg-base-100/50 odd:bg-white even:bg-[#F2FAFF]">
                                            <td className="sticky left-0 z-30 bg-inherit font-bold pt-dark-blue border-r border-base-100 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3">
                                                {name}
                                            </td>
                                            {items.map((p, idx) => (
                                                <td key={p.sku}
                                                    className="px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 text-[#044165]/80 font-medium align-top">
                                                    {vals[idx] || '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="modal-backdrop bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
        </div>
    );
};